import { type CandidateProfileCountsData, useCandidateProfileShow } from '@jobilla/nexus-sdk'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { sectionColours, sectionIcons, sectionKeys } from './candidate-profile-tab'

export function ProfileSectionCounts({ candidateId }: { candidateId: string }) {
	const profileQuery = useCandidateProfileShow(candidateId, { enabled: !!candidateId })
	const profile = profileQuery.data

	if (!profile) {
		return null
	}

	return (
		<ProfileCountsBadge
			counts={{
				workHistory: profile.workHistory.length,
				education: profile.education.length,
				licenses: profile.licenses.length,
			}}
		/>
	)
}

export function ProfileCountsBadge({ counts }: { counts: CandidateProfileCountsData | undefined }) {
	const { t } = useTranslation('common')

	if (!counts || sectionKeys.every(sectionKey => counts[sectionKey] === 0)) {
		return null
	}

	return (
		<span className="flex shrink-0 items-center gap-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
			{sectionKeys.map((sectionKey, sectionIndex) => {
				const Icon = sectionIcons[sectionKey]
				const total = counts[sectionKey]
				const isFilled = total > 0
				const label = t(`candidateCard.profileCounts.${sectionKey}`, { total })

				return (
					<Fragment key={sectionKey}>
						{sectionIndex > 0
							? <span aria-hidden={true} className="font-normal text-gray-400">/</span>
							: null}
						<span
							title={label}
							className={`flex items-center gap-1 ${isFilled ? '' : 'font-medium text-gray-400'}`}
						>
							<Icon
								className={`size-3 ${isFilled ? sectionColours[sectionKey].icon : 'text-gray-400'}`}
								aria-hidden={true}
							/>
							<span aria-hidden={true}>{total}</span>
							<span className="sr-only">{label}</span>
						</span>
					</Fragment>
				)
			})}
		</span>
	)
}
